import React, { useState } from 'react';
import styled from 'styled-components';
import { CropData } from '../types/Photo';

interface ProtectedImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  showWatermark?: boolean;
  watermarkText?: string;
  crop?: CropData;
}

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  width: fit-content;
  height: fit-content;
`;

const StyledImage = styled.img`
  display: block;
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
`;

// Transparent overlay to prevent right-click and selection
const ProtectionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
`;

const Watermark = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 6px 12px;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  border-radius: 4px;
  z-index: 2;
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
`;

const ProtectedImage: React.FC<ProtectedImageProps> = ({
  src,
  alt,
  className,
  loading = 'lazy',
  showWatermark = false,
  watermarkText = '© All Rights Reserved',
  crop,
}) => {
  const [contextMenuShown, setContextMenuShown] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  // Prevent right-click context menu
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenuShown(true);
    setTimeout(() => setContextMenuShown(false), 2000);
    return false;
  };

  // Prevent dragging
  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
    return false;
  };

  // Prevent selection
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Prevent touch and hold on mobile
  const handleTouchStart = (_e: React.TouchEvent) => {
    // Don't prevent default here as it would break scrolling
    // Just ensure the image itself can't be selected
  };

  // Handle image load to get dimensions for crop calculation
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  };

  // Calculate crop styles if crop data exists
  const getCropStyles = () => {
    if (!crop || !imageDimensions) return {};

    return {
      position: 'relative' as const,
      width: `${crop.width}px`,
      height: `${crop.height}px`,
      overflow: 'hidden' as const,
    };
  };

  const getImageStyles = () => {
    if (!crop || !imageDimensions) return {};

    return {
      position: 'absolute' as const,
      left: `-${crop.x}px`,
      top: `-${crop.y}px`,
      width: `${imageDimensions.width}px`,
      height: `${imageDimensions.height}px`,
    };
  };

  return (
    <ImageContainer
      className={className}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={crop ? getCropStyles() : {}}
    >
      <StyledImage
        src={src}
        alt={alt}
        loading={loading}
        draggable={false}
        onDragStart={handleDragStart}
        onLoad={handleImageLoad}
        style={crop && imageDimensions ? getImageStyles() : {}}
      />
      <ProtectionOverlay />
      {/* {showWatermark && <Watermark>{watermarkText}</Watermark>} */}
      {contextMenuShown && (
        <Watermark style={{ bottom: '50%', right: '50%', transform: 'translate(50%, 50%)' }}>
          Image protected - Download disabled
        </Watermark>
      )}
    </ImageContainer>
  );
};

export default ProtectedImage;
